import { useMemo } from 'react';

import { LoyaltyTierKey } from 'generated/graphql-gateway';
import { useAuth } from 'hooks/auth/use-auth';
import { selectors, useAppSelector } from 'state/global-state';

export enum ModalType {
  UPGRADE = 'upgrade',
  DOWNGRADE = 'downgrade',
}

let updateQueryCalled = false;

export const useLoyaltyUserTier = (): {
  loyaltyTierKey: LoyaltyTierKey;
  loyaltyTierExpiryDate: string | undefined;
  pointsEarned: number;
  loyaltyTierChanged: ModalType | null;
} => {
  const userDetails = useAppSelector(selectors.loyalty.selectLoyaltyUser);
  const { user, updateUserLastKnownLoyaltyTier } = useAuth();

  // The last known value that the user had
  const lastKnownUserTier = user?.details?.loyaltyTier;

  // The current loyalty user tier query
  const loyaltyUserTier = userDetails.loyaltyUser?.loyaltyTier?.loyaltyTierKey;

  const loyaltyTierChanged = useMemo(() => {
    // If no value comes from the loyalty API do nothing
    if (!loyaltyUserTier) {
      return null;
    }

    // If we don't have any value locally we set the remote value
    if (!lastKnownUserTier) {
      if (!updateQueryCalled) {
        updateUserLastKnownLoyaltyTier(loyaltyUserTier);
        updateQueryCalled = true;
      }
      return null;
    }

    // If the values are not null and are different, modal needs to be shown
    // If user went from Tier_1 to Tier_2, we will show the Upgrade Modal
    // If user went from Tier_2 to Tier_1 we will show the Downgrade Modal
    if (lastKnownUserTier !== loyaltyUserTier) {
      // Update the last known loyalty tier to the latest value
      if (!updateQueryCalled) {
        updateUserLastKnownLoyaltyTier(loyaltyUserTier);
        updateQueryCalled = true;
      }

      if (loyaltyUserTier === LoyaltyTierKey.TIER_2) {
        return ModalType.UPGRADE;
      } else {
        return ModalType.DOWNGRADE;
      }
    }
    return null;
  }, [lastKnownUserTier, loyaltyUserTier, updateUserLastKnownLoyaltyTier]);

  return {
    loyaltyTierKey: userDetails.loyaltyUser?.loyaltyTier?.loyaltyTierKey ?? LoyaltyTierKey.TIER_1,
    loyaltyTierExpiryDate: userDetails.loyaltyUser?.loyaltyTier?.loyaltyTierExpiryDate,
    pointsEarned: userDetails.loyaltyUser?.loyaltyTier?.pointsEarnedInTimeConstraint || 0,
    loyaltyTierChanged,
  };
};
